import React, { useState } from 'react';
import '../styles/GuestProfile.css';

const GuestProfileDemo = () => {
  // Enhanced demo data with bathroom-specific images
  const [guestData, setGuestData] = useState({
    name: 'Sarah Mars',
    profilePicture: 'https://randomuser.me/api/portraits/women/44.jpg',
    userType: 'gigWorker',
    memberSince: 'May 2025',
    totalSavings: '$24.50',
    currentSubscription: {
      plan: 'Premium',
      expires: '2025-12-31',
      price: '$9.99/month',
      bathroomsAllowed: 'Unlimited',
      benefits: [
        'Unlimited bathroom access',
        'Priority locations',
        'No wait times',
        'Exclusive discounts'
      ]
    },
    bathroomHistory: [
      {
        id: 1,
        name: 'Times Square Premium Restroom',
        address: '1540 Broadway, New York, NY 10036',
        date: '2025-06-15',
        time: '2:45 PM',
        price: '$0.00 (subscription)',
        rating: 4,
        photo: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', // Modern restroom
        features: ['ADA Accessible', 'Baby Changing', 'Premium Soap']
      },
      {
        id: 2,
        name: 'Port Authority Restrooms',
        address: '625 8th Ave, New York, NY 10018',
        date: '2025-06-10',
        time: '11:30 AM',
        price: '$1.50',
        rating: 3,
        photo: 'https://images.unsplash.com/photo-1551524559-8af4e6624178?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', // Luxury bathroom
        features: ['High Capacity', '24/7 Access']
      },
      {
        id: 3,
        name: 'Bryant Park Restroom',
        address: 'W 40th St, New York, NY 10018',
        date: '2025-05-28',
        time: '4:15 PM',
        price: '$0.00 (subscription)',
        rating: 5,
        photo: 'https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-1.2.1&auto=format&fit=crop&w=500&q=80', // Clean public toilet
        features: ['Free', 'Park Setting']
      }
    ],
    preferences: {
      promotions: true,
      notifications: true,
      favoriteLocations: ['Times Square Premium Restroom']
    }
  });

  // ... rest of the component code remains exactly the same ...

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });

  const handleSubscriptionChange = (newPlan) => {
    setGuestData(prev => ({
      ...prev,
      currentSubscription: {
        ...prev.currentSubscription,
        plan: newPlan,
        price: newPlan === 'Basic' ? '$4.99/month' : '$9.99/month',
        bathroomsAllowed: newPlan === 'Basic' ? '10/month' : 'Unlimited',
        benefits: newPlan === 'Basic' 
          ? ['10 bathrooms/month', 'Standard access']
          : ['Unlimited bathroom access', 'Priority locations', 'No wait times', 'Exclusive discounts']
      }
    }));
    setShowSubscriptionModal(false);
  };

  const addReview = (visitId) => {
    setGuestData(prev => ({
      ...prev,
      bathroomHistory: prev.bathroomHistory.map(visit => 
        visit.id === visitId 
          ? { ...visit, rating: newReview.rating, review: newReview.comment }
          : visit
      )
    }));
    setNewReview({ rating: 5, comment: '' });
  };

  const renderStars = (rating, size = 'medium') => {
    const sizeClass = size === 'large' ? 'star-large' : 'star-medium';
    return (
      <div className={`star-rating ${sizeClass}`}>
        {[1, 2, 3, 4, 5].map(star => (
          <span 
            key={star} 
            className={star <= rating ? 'star filled' : 'star'}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="guest-profile-container demo-version">
      <div className="demo-banner">
        <h2>GUEST PROFILE DEMO</h2>
        <p>Showing sample data for demonstration purposes</p>
      </div>

      <div className="profile-header">
        <div className="profile-picture-container">
          <img 
            src={guestData.profilePicture} 
            alt="Profile" 
            className="profile-picture"
          />
          <button className="edit-picture-button">Edit</button>
        </div>
        
        <div className="profile-info">
          <h1>{guestData.name}</h1>
          <div className="user-type-badge">
            {guestData.userType === 'tourist' ? '✈️ Tourist' : '💼 Gig Worker'}
          </div>
          <div className="member-info">
            <p>Member since {guestData.memberSince}</p>
            <p className="savings">Estimated savings: {guestData.totalSavings}</p>
          </div>
          <button className="edit-profile-button">Edit Profile</button>
        </div>
      </div>

      <div className="profile-section">
        <h2>My Subscription</h2>
        <div className={`subscription-card ${guestData.currentSubscription.plan.toLowerCase()}`}>
          <div className="subscription-header">
            <h3>{guestData.currentSubscription.plan} Plan</h3>
            <p className="price">{guestData.currentSubscription.price}</p>
          </div>
          <p className="bathrooms-allowed">{guestData.currentSubscription.bathroomsAllowed}</p>
          <p className="expires">Renews on {guestData.currentSubscription.expires}</p>
          
          <div className="subscription-benefits">
            <h4>Plan Benefits:</h4>
            <ul>
              {guestData.currentSubscription.benefits.map((benefit, index) => (
                <li key={index}>{benefit}</li>
              ))}
            </ul>
          </div>
          
          <div className="subscription-actions">
            <button 
              className="change-plan-button"
              onClick={() => setShowSubscriptionModal(true)}
            >
              Change Plan
            </button>
            <button className="cancel-button">Cancel Subscription</button>
          </div>
        </div>
      </div>

      <div className="profile-section">
        <h2>Recent Bathroom Visits</h2>
        <div className="history-list">
          {guestData.bathroomHistory.map(visit => (
            <div key={visit.id} className="history-item">
              <div className="history-photo">
                <img src={visit.photo} alt={visit.name} />
              </div>
              <div className="history-details">
                <div className="history-main">
                  <h4>{visit.name}</h4>
                  <p className="address">{visit.address}</p>
                  <p className="date">{visit.date} at {visit.time}</p>
                  <p className="price">{visit.price}</p>
                </div>
                <div className="history-features">
                  {visit.features.map((feature, i) => (
                    <span key={i} className="feature-tag">{feature}</span>
                  ))}
                </div>
                <div className="history-review">
                  {visit.review ? (
                    <div className="completed-review">
                      {renderStars(visit.rating)}
                      <p>"{visit.review}"</p>
                    </div>
                  ) : (
                    <div className="add-review">
                      <h5>Rate this visit:</h5>
                      <div className="star-input">
                        {[1, 2, 3, 4, 5].map(star => (
                          <span 
                            key={star} 
                            className={star <= newReview.rating ? 'star filled' : 'star'}
                            onClick={() => setNewReview({...newReview, rating: star})}
                          >
                            ★
                          </span>
                        ))}
                      </div>
                      <textarea
                        placeholder="Write your review..."
                        value={newReview.comment}
                        onChange={(e) => setNewReview({...newReview, comment: e.target.value})}
                      />
                      <button onClick={() => addReview(visit.id)}>Submit Review</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="profile-section">
        <h2>My Preferences</h2>
        <div className="preferences">
          <div className="preference-card">
            <h4>Notifications</h4>
            <div className="preference-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={guestData.preferences.promotions} 
                  onChange={() => setGuestData(prev => ({
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      promotions: !prev.preferences.promotions
                    }
                  }))} 
                />
                Receive promotional offers
              </label>
            </div>
            <div className="preference-item">
              <label>
                <input 
                  type="checkbox" 
                  checked={guestData.preferences.notifications} 
                  onChange={() => setGuestData(prev => ({
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      notifications: !prev.preferences.notifications
                    }
                  }))}
                />
                Notify me about new bathrooms in my area
              </label>
            </div>
          </div>
          
          <div className="preference-card">
            <h4>Favorite Locations</h4>
            {guestData.preferences.favoriteLocations.length > 0 ? (
              <ul className="favorites-list">
                {guestData.preferences.favoriteLocations.map((fav, i) => (
                  <li key={i}>
                    {fav}
                    <button 
                      className="remove-favorite"
                      onClick={() => setGuestData(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          favoriteLocations: prev.preferences.favoriteLocations.filter(f => f !== fav)
                        }
                      }))}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-favorites">You haven't favorited any locations yet</p>
            )}
          </div>
        </div>
      </div>

      {showSubscriptionModal && (
        <div className="subscription-modal">
          <div className="modal-content">
            <h3>Change Your Subscription Plan</h3>
            <div className="plan-options">
              <div className={`plan-card ${guestData.currentSubscription.plan === 'Basic' ? 'current' : ''}`}>
                <h4>Basic Plan</h4>
                <p className="price">$4.99/month</p>
                <p>10 bathrooms per month</p>
                <ul>
                  <li>Standard access</li>
                  <li>Pay-as-you-go after limit</li>
                </ul>
                <button 
                  onClick={() => handleSubscriptionChange('Basic')}
                  disabled={guestData.currentSubscription.plan === 'Basic'}
                >
                  {guestData.currentSubscription.plan === 'Basic' ? 'Current Plan' : 'Select Basic'}
                </button>
              </div>
              
              <div className={`plan-card ${guestData.currentSubscription.plan === 'Premium' ? 'current' : ''}`}>
                <h4>Premium Plan</h4>
                <p className="price">$9.99/month</p>
                <p>Unlimited access</p>
                <ul>
                  <li>Priority locations</li>
                  <li>No wait times</li>
                  <li>Exclusive discounts</li>
                </ul>
                <button 
                  onClick={() => handleSubscriptionChange('Premium')}
                  disabled={guestData.currentSubscription.plan === 'Premium'}
                >
                  {guestData.currentSubscription.plan === 'Premium' ? 'Current Plan' : 'Select Premium'}
                </button>
              </div>
            </div>
            <button 
              className="close-modal"
              onClick={() => setShowSubscriptionModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestProfileDemo;